import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { OnboardingStatus, ProfileCache, SelectorOrganization, UserData } from '@tgx/shared/interfaces';
import {
  CacheService,
  PlatformBrowserService,
  PlatformInfoService,
  PlatformSelectorOrganizationService,
  AuthService,
} from '@tgx/shared/services';
import { getHeaderMenuUserName } from '@tgx/shared/utils';

@Component({
  selector: 'tgx-onboarding-implementation-header',
  templateUrl: './onboarding-implementation-header.component.html',
  styleUrls: ['./onboarding-implementation-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OnboardingImplementationHeaderComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private cacheService = inject(CacheService);
  private authService = inject(AuthService);
  private platformBrowserService = inject(PlatformBrowserService);
  private platformInfoService = inject(PlatformInfoService);
  private platformSelectorOrganizationService = inject(PlatformSelectorOrganizationService);

  // User and organization data
  @Input() companyName: string;
  @Input() userImg: string;
  @Input() loggedUser: UserData;
  private organization: SelectorOrganization;
  private profile: ProfileCache;
  protected isBuyerAndSeller = false;
  protected userName: string;
  protected bearerCopied = false;
  private bearerTimeoutId: number | null = null;

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.platformInfoService.organizationSelected$.pipe(takeUntil(this.destroy$)).subscribe(async (org) => {
      this.organization = org as SelectorOrganization;

      if (!org?.code) {
        this.isBuyerAndSeller = false;
        return;
      }

      this.profile = (await this.cacheService.retrieveProfilesByCodes([org.code]))[0];
      this.isBuyerAndSeller = this.shouldShowOnboardingSwitchAlert(this.profile);
    });
    this.platformInfoService.selectedPartnerType$.pipe(takeUntil(this.destroy$)).subscribe((type) => {
      if (this.organization) {
        this.organization.typeSelected = type;
      }
    });
    this.userName = getHeaderMenuUserName(this.loggedUser);
  }

  private shouldShowOnboardingSwitchAlert(profile: ProfileCache | null | undefined): boolean {
    const buyerStatus = profile?.buyerStatus ?? OnboardingStatus.None;
    const sellerStatus = profile?.sellerStatus ?? OnboardingStatus.None;

    const isDualPartner = buyerStatus !== OnboardingStatus.None && sellerStatus !== OnboardingStatus.None;
    const hasMixedOnboardingState =
      (buyerStatus === OnboardingStatus.Pending && sellerStatus === OnboardingStatus.Completed) ||
      (buyerStatus === OnboardingStatus.Completed && sellerStatus === OnboardingStatus.Pending);

    return isDualPartner && hasMixedOnboardingState;
  }

  protected async changeOrganizationType(): Promise<void> {
    if (!this.organization?.code) {
      return;
    }

    const buyerStatus = this.profile?.buyerStatus ?? OnboardingStatus.None;
    const sellerStatus = this.profile?.sellerStatus ?? OnboardingStatus.None;

    // In onboarding alert context, switch to the completed side when one side is pending.
    let nextType = this.organization.typeSelected === 'seller' ? 'buyer' : 'seller';
    this.platformInfoService.setPartnerType(this.organization.typeSelected);
    if (buyerStatus === OnboardingStatus.Pending && sellerStatus !== OnboardingStatus.Pending) {
      nextType = 'seller';
    } else if (buyerStatus !== OnboardingStatus.Pending && sellerStatus === OnboardingStatus.Pending) {
      nextType = 'buyer';
    }

    this.platformBrowserService.setLocalStorageItem('organizationType', nextType);
    await this.platformSelectorOrganizationService.setOrganization(this.organization.code);
    await this.router.navigate(['/dashboard']);
  }

  protected async signOut(): Promise<void> {
    await this.authService.logout();
  }

  ngOnDestroy(): void {
    if (this.bearerTimeoutId) {
      clearTimeout(this.bearerTimeoutId);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected async copyBearerToClipboard(): Promise<void> {
    if (!navigator.clipboard?.writeText) {
      this.resetBearerCopied();
      console.error('Clipboard API is not available in this environment.');
      return;
    }
    try {
      await navigator.clipboard.writeText(this.platformBrowserService.getToken());
      this.resetBearerCopied();
      this.bearerCopied = true;
      this.bearerTimeoutId = window.setTimeout(() => this.resetBearerCopied(), 2000);
    } catch (error) {
      this.resetBearerCopied();
      console.error('Failed to copy bearer token to clipboard.', error);
    }
  }

  private resetBearerCopied(): void {
    if (this.bearerTimeoutId) {
      clearTimeout(this.bearerTimeoutId);
      this.bearerTimeoutId = null;
    }
    this.bearerCopied = false;
  }
}
